import { ERowTypes } from "api/enums";
import {
  ValueGreaterThanBalanceException,
  ZeroUnitValueException,
} from "api/exceptions/MeasurementException";
import MeasurementModel from "api/models/MeasurementModel";
import MeasurementRowModel from "api/models/MeasurementRowModel";
import axios from "axios";
import { decimal } from "utils/DecimalPrecision";
import CalculateMeasurementRowService from "./CalculateMeasurementRowService";

class CalculateMeasurementService {
  /**
   * Chama o serviço que fará o recálculo e atualização
   * de todas as linhas da medição.
   */
  async recalculateAllRows(
    contractId: string,
    measurementId: string
  ): Promise<void> {
    if (!contractId || !measurementId) return;

    const data = {
      contractId,
      measurementId,
    };

    await axios
      .post(`${process.env.REACT_APP_RECALCULATE_ALL_MENSUREMENT}`, data)
      .catch((error) => {
        console.error(`Error on recalculate measurements rows: `, error);
      });
  }

  /**
   * Calcula as linhas da medição de acordo com o valor alterado.
   * @returns Objeto com os dados das linhas calculadas para atualização no banco de dados
   * e o novo total da medição e o novo total acumulado.
   */
  async calculateRows(
    measurement: MeasurementModel,
    measuredRow: MeasurementRowModel,
    measuredField: string,
    measuredValue: number,
    affectedRows: MeasurementRowModel[],
    currentMeasurementTotal: number,
    currentAccumulatedTotal: number,
    validateBalance: boolean
  ) {
    if (
      measuredRow.rowType === ERowTypes.RESOURCE &&
      measuredRow.contractRow?.unitCost === 0
    ) {
      throw new ZeroUnitValueException();
    }

    const oldTotal = measuredRow.total;
    const calculatedRows = await this.fetchCalculateMeasurement(
      measurement.uid,
      measuredRow.uid,
      measuredValue,
      measuredField,
      affectedRows,
      validateBalance
    );

    calculatedRows.forEach((rowCalculated) =>
      this.updateRowWithAPIData(affectedRows, rowCalculated)
    );

    // Obtém o total atualizado da medição
    const newTotal = this.calculateMeasurementTotalByDiff(
      currentMeasurementTotal,
      measuredRow.total,
      oldTotal
    );

    // Obtém o total acumulado atualizado da medição
    const newAccumulatedTotal = this.calculateMeasurementAccumulatedTotalByDiff(
      currentAccumulatedTotal,
      measuredRow.total,
      oldTotal
    );

    return { rows: calculatedRows, newTotal, newAccumulatedTotal };
  }

  /**
   * Chama o serviço que calcula os dados da medição enviando
   * os dados das linhas afetadas e depois atualiza as mesma
   * com os valores calculados.
   * @returns {Promise<any[]>} Dados das linhas calculadas
   */
  private async fetchCalculateMeasurement(
    measurementUid: string,
    measuredRowUid: string,
    measuredValue: number,
    measuredField: string,
    affectedRows: MeasurementRowModel[],
    validateBalance: boolean
  ): Promise<any[]> {
    measuredValue =
      measuredField === "percentual" ? measuredValue / 100 : measuredValue;

    if (
      validateBalance &&
      measuredField === "percentual" &&
      (measuredValue > 1.0 || measuredValue < 0)
    ) {
      console.error(
        `Percentual inválido! Deve se informar um valor entre 0 e 1.`
      );
      return [];
    }

    const data = {
      measureID: measurementUid,
      measure: {
        uid: measuredRowUid,
        total: measuredValue,
        field: measuredField,
        validateBalance,
      },
      wbs: affectedRows.map((row) => this.getMesurementData(row)),
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_CALCULATE_MENSUREMENT}`,
        data
      );

      return response ? response.data.data.rows : [];
    } catch (error: any) {
      if (error.response) {
        const message = String(error.response.data?.error || "");
        console.error(`Error on calculate measurements rows: ${message}`);

        if (message.includes("Valor maior que o saldo")) {
          throw new ValueGreaterThanBalanceException();
        }
      }
      return [];
    }
  }

  /**
   * Monta o objeto com os dados necessários para serem
   * enviados ao serviço de cálculo da medição.
   * @param {MeasurementRowModel} row Linha da medição
   * @returns {any} Objeto com os dados necessários para o cálculo.
   */
  private getMesurementData(row: MeasurementRowModel) {
    const data: any = {
      uid: row.uid,
      structured: row.structured,
      rowType: row.rowType,
      unitCost: row.contractRow?.unitCost,
      quantity: row.contractRow?.quantity,
      total: row.contractRow?.total,
    };

    if (row) {
      data.createdAt = row.createdAt;
      data.measured = { ...row.measured };

      if (row.isFilled) {
        data.currentMeasurement = {
          total: row.currentMeasurement.total,
          quantity: row.currentMeasurement.quantity,
        };
      }
    }
    return data;
  }

  /**
   * Procura a linha correspondente aos dados calculados e atualiza seus valores
   * medidos, depois a marca com o status de 'changed'.
   * @param {MeasurementRowModel[]} rows Lista com as linhas da medição
   * @param {any} rowData Dados da linha recalculada
   */
  private updateRowWithAPIData(rows: MeasurementRowModel[], rowData: any) {
    let { structured, currentMeasurement } = rowData;
    const row = rows.find((row) => row.structured === structured);
    if (row) {
      CalculateMeasurementRowService.calculateMeasurementValues(
        row,
        currentMeasurement || { total: 0 }
      );
      const contract = row.contractRow;
      if (contract) {
        contract.setMeasuredOf(row.measurementUid, row.total, row.quantity);
      }
      row.setRowStatusToChanged();
    }
  }

  /**
   * Calcula o novo total da medição a partir da diferença
   * entre os totais de uma linha da medição.
   * @param {number} measurementTotal Total da medição
   * @param {number} newRowTotal Novo total de uma linha
   * @param {number} oldRowTotal Antigo total de uma linha
   * @returns Novo total da medição.
   */
  private calculateMeasurementTotalByDiff(
    measurementTotal: number,
    newRowTotal: number,
    oldRowTotal: number
  ): number {
    const diffTotal = decimal(newRowTotal).subtract(oldRowTotal).toNumber();
    let total = decimal(measurementTotal).sum(diffTotal).toNumber();
    if (decimal(total, 2).isEqualTo(0)) {
      total = 0;
    }
    return total;
  }

  /**
   * Calcula o novo acumulado total a partir da diferença
   * entre os totais de uma linha da medição.
   * @param {number} accumulatedTotal Total acumulado da medição
   * @param {number} newRowTotal Novo total de uma linha
   * @param {number} oldRowTotal Antigo total de uma linha
   * @returns Novo total acumulado da medição.
   */
  private calculateMeasurementAccumulatedTotalByDiff(
    accumulatedTotal: number,
    newRowTotal: number,
    oldRowTotal: number
  ): number {
    const diffTotal = decimal(newRowTotal).subtract(oldRowTotal).toNumber();
    let total = decimal(accumulatedTotal).sum(diffTotal).toNumber();
    if (decimal(total, 2).isEqualTo(0)) {
      total = 0;
    }
    return total;
  }
}

export default new CalculateMeasurementService();
